import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTop from '@/components/base/BackToTop';
import { aboutInfo } from '@/mocks/manga';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9on0iqftmtnfcfh0a60';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormState('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      const isSpam = serverMsg.includes('spam') || serverMsg.includes('form data is spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormState('success');
        form.reset();
      } else {
        setFormState('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
              关于樱花漫画
            </h1>
            <div className="w-16 h-1 bg-primary-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              {aboutInfo.brandIntro}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed">
              {aboutInfo.mission}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 bg-background-50 rounded-lg">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">听听漫画爱好者们怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="p-6 bg-background-100 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-xs text-accent-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="bg-background-50 rounded-lg p-6 md:p-8">
              {formState === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-primary-100 rounded-full">
                    <i className="ri-check-line text-3xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      name="subject"
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formState === 'error' && (
                    <div className="mb-4 p-3 bg-accent-50 border border-accent-200/70 rounded-lg">
                      <p className="text-sm text-accent-700">{formError || '提交失败，请稍后再试'}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="w-full py-3 text-sm font-bold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {formState === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-1.5"></i>提交中...
                      </>
                    ) : (
                      '提交留言'
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                  <i className="ri-wechat-line text-lg text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-lg text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">公司地址</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}